import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { IoMdEye } from "react-icons/io";
import { MdFilterAlt } from "react-icons/md";
import { CiSearch } from "react-icons/ci";
import {
  getAllChats,
  createMessage,
  getMessages,
  addParticipant,
  markMessageAsRead,
} from "../../../api/common/chat";
import { BiSolidBot } from "react-icons/bi";
import { Link } from "react-router-dom";
import CustomerSupportIcon from "../../../helper/images/customer.png";
import { useAuth } from "../../../context/AuthContext";
import UserImage from "../../../helper/images/customer.png";
import socket from "../../../helper/socket";
import SupportChat from "../../../Layout/Chat";
import Queries from "../../../Layout/Queries";
import { useTranslation } from "../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const CustomerSupport = () => {
  const { t } = useTranslation();
  const translations = t("Support");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [status, setStatus] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const queryClient = useQueryClient();
  const { userData } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const debouncedMarkAsReadRef = useRef();
  const [search , setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  // Debounce search input (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["allChats", debouncedSearch],
    queryFn: ()=> getAllChats(debouncedSearch),
  });

  const {
    data: messagesData,
    isLoading: isChatLoading,
    refetch: refetchMessages,
  } = useQuery({
    queryKey: ["all-messages", selectedChat?._id],
    enabled: !!selectedChat?._id,
    queryFn: async () => {
      try {
        return await getMessages(selectedChat._id);
      } catch (error) {
        const errorMsg = error?.response?.data?.message;
        if (errorMsg === "You are not authorized to view these messages") {
          await addParticipant({
            chatId: selectedChat._id,
            userId: userData?.id,
            role: "admin",
          });

          return await getMessages(selectedChat._id);
        } else {
          throw error;
        }
      }
    },
  });

  const chats = data?.data;
  const messages = messagesData?.data?.messages;

  const messageMutation = useMutation({
    mutationFn: createMessage,
    onSuccess: () => {
      queryClient.invalidateQueries(["allChats"]);
      setInputMessage("");
    },
  });

  const messageReadMutation = useMutation({
    mutationFn: markMessageAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries(["allChats"]);
      setInputMessage("");
    },
  });

  useEffect(() => {
    if (selectedChat?._id) {
      socket.emit("joinChat", selectedChat._id);
      messageReadMutation.mutate(selectedChat?._id);
    }

    return () => {
      if (selectedChat?._id) {
        socket.emit("leaveRoom", selectedChat._id);
      }
    };
  }, [selectedChat?._id]);

  useEffect(() => {
    const handleReceiveMessage = (newMessage) => {
      queryClient.invalidateQueries(["allChats"]);
    };
    socket.on("receiveMessage", handleReceiveMessage);
    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
    };
  }, []);

  useEffect(() => {
    const handleReceiveMessage = (newMessage) => {
      if (newMessage.chat === selectedChat?._id) {
        queryClient.invalidateQueries(["all-messages", selectedChat._id]);

        if (debouncedMarkAsReadRef.current) {
          debouncedMarkAsReadRef.current(selectedChat._id);
        }
      }
    };

    const handleMessageRead = ({ chatId, userId }) => {
      if (chatId === selectedChat?._id) {
        queryClient.setQueryData(["all-messages", chatId], (oldData) => {
          if (!oldData?.data?.messages) return oldData;

          const updatedMessages = oldData.data.messages.map((msg) => {
            if (!msg.readBy?.includes(userId)) {
              return {
                ...msg,
                readBy: [...msg.readBy, userId],
              };
            }
            return msg;
          });

          return {
            ...oldData,
            data: {
              ...oldData.data,
              messages: updatedMessages,
            },
          };
        });
      }
    };

    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("messageRead", handleMessageRead);

    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("messageRead", handleMessageRead);
    };
  }, [selectedChat?._id]);

  const { mutate: addUserToChat } = useMutation({
    mutationFn: addParticipant,
    onSuccess: (data) => {
      
    },
    onError: (error) => {
      console.error("Failed to add participant", error);
    },
  });

  const handleSwitchToggle = (id) => {
    const newValue = !enabled;
    if (selectedChat?.participants?.length > 2) {
      return false;
    }
    setEnabled(newValue);

    if (newValue) {
      addUserToChat({
        chatId: selectedChat?._id,
        userId: id,
      });
    }
  };

  const handleFilterChange = (value) => {
    setStatus(value);
    setIsOpen(false);
  };

  const handleView = (chat) => {
    setSelectedChat(chat);
    setIsPopupOpen(true);

    if (chat?.participants?.length > 2) {
      setEnabled(true);
    }
  };

  const totalParticipants = selectedChat?.participants?.length || 1;

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  useEffect(() => {
    debouncedMarkAsReadRef.current = debounce((chatId) => {
      messageReadMutation.mutate(chatId);
    }, 500);

    return () => {
      debouncedMarkAsReadRef.current = null;
    };
  }, []);

  // const statuses = [
  //   t("support").statuses.inProgress,
  //   t("support").statuses.pending,
  //   t("support").statuses.resolved,
  // ];

  const statusKeys = [
    `${translations?.InProgress}`,
    `${translations?.Pending}`,
    `${translations?.Resolved}`,
  ];
  // const statuses = statusKeys.map((key) => t(`(Support).statuses.${key}`));

  return (
    <>
      <div className="flex justify-between items-center flex-wrap gap-[20px]">
        <h2 className="text-[22px] font-medium">
          {/* {t("support").customerSupport} */}
        </h2>
        {/* <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="py-[9px] px-[35px] bg-white border border-[#DDDDDD] flex items-center gap-2 rounded-[2px]"
          >
            <MdFilterAlt /> {status || `${translations?.Filter}`}
          </button>
          {isOpen && (
            <div className="absolute mt-2 w-36 bg-white border rounded shadow text-sm font-medium z-10">
              {statusKeys?.map((item) => (
                <div
                  key={item}
                  onClick={() => handleFilterChange(item)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  {item}
                </div>
              ))}
            </div>
          )}
        </div> */}
      </div>

      <Queries chats={chats} isLoading={isLoading} handleView={handleView} search={search} setSearch={setSearch} />

      {/* Modal */}
      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <SupportChat
          selectedChat={selectedChat}
          messages={messages}
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          messageMutation={messageMutation}
          userData={userData}
          totalParticipants={totalParticipants}
          enabled={enabled}
          handleSwitchToggle={handleSwitchToggle}
        />
      </PopupModal>
    </>
  );
};

export default CustomerSupport;
